import { useState, useEffect, useCallback } from "react";
import { siteKey } from "../lib/siteData";
import { loadDataForSeoCredentials, loadAnthropicKey } from "./SettingsModal";
import { showAlert } from "../lib/dialog";

// Citation Check — searches Google for the site's domain, reads each listing that references it,
// extracts the business info (NAP) each one shows, and flags any field that differs from the
// business's own info (Name/Address/Website from the cover section; Phone from its Google Business
// Profile). Only pages that present ≥2 NAP data points are treated as real listings.

interface Props { siteUrl?: string; siteBrand?: string }

interface CitationField { value: string; matches: boolean | null }
interface CitationProfile {
  url: string;
  domain: string;
  title: string;
  fields: { name?: CitationField; phone?: CitationField; website?: CitationField; address?: CitationField };
  hasMismatch: boolean;
  fromPreview?: boolean; // the site blocked the download — read from Google's result title + snippet
}
interface CitationCheckPayload {
  business: { name: string; website: string; address: string; city: string; phone: string; phoneSource: string };
  profiles: CitationProfile[];
  counts: { checked: number; kept: number; consistent: number; mismatched: number };
  notes: string[];
  ranAt?: string;
  // Breakdown of every page checked (absent on results saved before it was added).
  searches?: Array<{ via: "domain" | "name"; query: string; found: number; error?: string }>;
  pages?: Array<{ url: string; domain: string; title: string; via: "domain" | "name"; rank: number; outcome: PageOutcome; detail?: string }>;
}
type PageOutcome = "kept" | "fetch_failed" | "empty" | "read_failed" | "not_business" | "too_few";

const OUTCOME_LABEL: Record<PageOutcome, { text: string; color: string }> = {
  kept:         { text: "Kept as a citation", color: "#16a34a" },
  fetch_failed: { text: "Couldn't download the page", color: "#dc2626" },
  empty:        { text: "No readable content (likely built with JavaScript)", color: "#d97706" },
  read_failed:  { text: "Claude couldn't read the page", color: "#dc2626" },
  not_business: { text: "Not this business's listing", color: "var(--ink3)" },
  too_few:      { text: "Too few matching details", color: "#d97706" },
};

const FIELD_ORDER: { key: keyof CitationProfile["fields"]; label: string }[] = [
  { key: "name", label: "Name" },
  { key: "phone", label: "Phone" },
  { key: "website", label: "Website" },
  { key: "address", label: "Address" },
];

interface BizInfo { name: string; website: string; address: string; city: string; phone: string }

const coverBiz = (): Omit<BizInfo, "phone"> => ({
  name: (localStorage.getItem("cover_business_name") ?? "").trim(),
  website: (localStorage.getItem("cover_website_url") ?? "").trim(),
  address: (localStorage.getItem("cover_full_address") ?? "").trim(),
  city: (localStorage.getItem("cover_location") ?? "").trim(),
});

// The business-info fields are user-editable. Load this site's saved edits if present, otherwise
// seed Name/Website/Address/City from the cover section (phone is user-provided — no cover field).
const loadBiz = (bizKey: string): BizInfo => {
  try {
    const raw = bizKey ? localStorage.getItem(bizKey) : null;
    if (raw) {
      const b = JSON.parse(raw) as Partial<BizInfo>;
      return { name: b.name ?? "", website: b.website ?? "", address: b.address ?? "", city: b.city ?? "", phone: b.phone ?? "" };
    }
  } catch { /* ignore corrupt */ }
  return { ...coverBiz(), phone: "" };
};

const domainOf = (raw: string): string => {
  if (!raw) return "";
  try { return new URL(/^https?:\/\//i.test(raw) ? raw : `https://${raw}`).hostname.replace(/^www\./i, "").toLowerCase(); }
  catch { return raw.replace(/^https?:\/\//i, "").replace(/^www\./i, "").split("/")[0].toLowerCase(); }
};

// The citation-check run is tracked at MODULE level (keyed by the per-site cache key) so it survives
// the component unmounting on a tab switch: the result is already written straight to localStorage,
// and the mount effect re-attaches to restore the spinner and status (and never double-runs).
type CcResult = { ok: true; kept: number; mismatched: number } | { ok: false; error: string };
const ccRuns = new Map<string, Promise<CcResult>>();

export function CitationCheck({ siteUrl }: Props) {
  const key = siteUrl ? siteKey(siteUrl) : "";
  const cacheKey = `citation_check_${key}`;
  const bizKey = `citation_check_biz_${key}`;
  const [biz, setBiz] = useState<BizInfo>(() => loadBiz(bizKey));
  const [result, setResult] = useState<CitationCheckPayload | null>(null);
  const [running, setRunning] = useState(false);
  const [status, setStatus] = useState<{ tone: "info" | "err"; text: string } | null>(null);

  // Load this site's saved business-info edits (seeded from the cover) + cached result on site change.
  useEffect(() => {
    setBiz(loadBiz(bizKey));
    setStatus(null);
    setResult(null);
    if (!key) return;
    try {
      const raw = localStorage.getItem(cacheKey);
      if (raw) setResult(JSON.parse(raw) as CitationCheckPayload);
    } catch { /* ignore corrupt cache */ }
  }, [key, cacheKey, bizKey]);

  // Update one business-info field and persist the edited set for this site.
  const setField = (field: keyof BizInfo, value: string) => {
    setBiz(prev => {
      const nextBiz = { ...prev, [field]: value };
      try { if (key) localStorage.setItem(bizKey, JSON.stringify(nextBiz)); } catch { /* quota */ }
      return nextBiz;
    });
  };

  // "Done" status line (shared by the trigger and the re-attach effect).
  const ccDone = (kept: number, mismatched: number): { tone: "info"; text: string } =>
    ({ tone: "info", text: `Done — ${kept} potential citation${kept === 1 ? "" : "s"} found, ${mismatched} with inconsistencies.` });

  const run = useCallback(async () => {
    const domain = domainOf(biz.website || siteUrl || "");
    if (!domain) { void showAlert("Add the website URL in the cover section first.", { title: "Missing Website" }); return; }
    const { login, password } = loadDataForSeoCredentials();
    const anthropicKey = loadAnthropicKey();
    if (!login || !password) { void showAlert("DataForSEO credentials are required. Add them in ⚙ Settings → DataForSEO.", { title: "Credentials Required" }); return; }
    if (!anthropicKey) { void showAlert("Anthropic API key is required. Add it in ⚙ Settings → API Keys.", { title: "API Key Required" }); return; }
    if (running || ccRuns.has(cacheKey)) return; // already running (possibly from before a tab switch)

    setRunning(true);
    setStatus({ tone: "info", text: "Searching Google and reading each listing… this can take up to a minute." });
    // Run as a module-level promise so it completes and caches even if the component unmounts on a
    // tab switch; the result is written straight to localStorage (unmount-safe).
    const p = (async (): Promise<CcResult> => {
      const res = await fetch("/api/citation-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          domain, businessName: biz.name, address: biz.address, website: biz.website, location: biz.city, phone: biz.phone,
          anthropicKey, dfsLogin: login, dfsPassword: password,
        }),
      });
      const data = await res.json() as (CitationCheckPayload & { ok: boolean; error?: string });
      if (!res.ok || !data.ok) return { ok: false, error: data.error ?? "Citation check failed." };
      const payload: CitationCheckPayload = { business: data.business, profiles: data.profiles, counts: data.counts, notes: data.notes, searches: data.searches, pages: data.pages, ranAt: new Date().toLocaleString() };
      try { localStorage.setItem(cacheKey, JSON.stringify(payload)); } catch { /* quota — skip cache */ }
      return { ok: true, kept: payload.counts.kept, mismatched: payload.counts.mismatched };
    })();
    ccRuns.set(cacheKey, p);
    void p.catch(() => {}).finally(() => ccRuns.delete(cacheKey));
    try {
      const r = await p;
      try { const raw = localStorage.getItem(cacheKey); if (raw) setResult(JSON.parse(raw) as CitationCheckPayload); } catch { /* ignore */ }
      setStatus(r.ok ? ccDone(r.kept, r.mismatched) : { tone: "err", text: r.error });
    } catch (err) {
      setStatus({ tone: "err", text: `Network error: ${err instanceof Error ? err.message : "request failed"}` });
    } finally {
      setRunning(false);
    }
  }, [biz, siteUrl, cacheKey, running]);

  // Re-attach to a citation-check run still in flight from before a tab switch: restore the spinner
  // and apply the cached result when it finishes.
  useEffect(() => {
    if (!key) return;
    const p = ccRuns.get(cacheKey);
    if (!p) return;
    let alive = true;
    setRunning(true);
    setStatus({ tone: "info", text: "Searching Google and reading each listing… this can take up to a minute." });
    p.then(r => {
      if (!alive) return;
      try { const raw = localStorage.getItem(cacheKey); if (raw) setResult(JSON.parse(raw) as CitationCheckPayload); } catch { /* ignore */ }
      setStatus(r.ok ? ccDone(r.kept, r.mismatched) : { tone: "err", text: r.error });
    })
      .catch(err => { if (alive) setStatus({ tone: "err", text: `Network error: ${err instanceof Error ? err.message : "request failed"}` }); })
      .finally(() => { if (alive) setRunning(false); });
    return () => { alive = false; };
  }, [key, cacheKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const editRow = (label: string, field: keyof BizInfo, placeholder: string) => (
    <div style={{ display: "flex", gap: 10, alignItems: "center", padding: "4px 0" }}>
      <span style={{ width: 90, flexShrink: 0, color: "var(--ink3)", fontWeight: 700, textTransform: "uppercase", fontSize: 12, letterSpacing: .5 }}>{label}</span>
      <input
        value={biz[field]}
        onChange={e => setField(field, e.target.value)}
        placeholder={placeholder}
        style={{ flex: 1, padding: "7px 10px", border: "1px solid var(--bdr)", borderRadius: 5, fontSize: 12.5, fontFamily: "'Inter','Mulish',sans-serif", color: "var(--ink)", background: "var(--bg)", boxSizing: "border-box" }}
      />
    </div>
  );

  const badge = (m: boolean | null) => {
    if (m === true)  return <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 11 }}>✓ matches</span>;
    if (m === false) return <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 11 }}>✗ differs</span>;
    return <span style={{ color: "var(--ink4)", fontWeight: 600, fontSize: 11 }}>· not verified</span>;
  };

  return (
    <div style={{ maxWidth: 980, margin: "0 auto", padding: "18px 20px 60px", fontFamily: "'Inter','Mulish',sans-serif" }}>
      <div style={{ marginBottom: 6 }}>
        <h2 className="cd-hd-brand" style={{ margin: 0, fontSize: 20, fontWeight: 500, color: "#f97316", fontFamily: "'Oswald','Barlow Condensed','Inter',sans-serif", textTransform: "uppercase", letterSpacing: ".7px", display: "inline-flex", alignItems: "center" }}>
          Indexed Citations Check
          <span className="audit-tip">
            <span className="audit-tip-icon">?</span>
            <span className="audit-tip-box">
              <strong>How this crawl works</strong>
              <p>It searches Google with <code>"yourdomain.com" -site:yourdomain.com</code> for the top 80 external pages that mention your domain, and with <code>"Business Name" -site:yourdomain.com</code> for the top 50 that mention your business name — the <code>-site:</code> operator excludes your own website's pages.</p>
              <p>Each page is fetched and read, and kept only if it shows at least two of your business name, phone, website, or address — a "Visit Website" button linking to your site counts as showing the website. That isolates real citation-style listings from passing mentions (blogs, news, and articles are dropped).</p>
              <p>Each kept page's Name, Phone, Website, and Address are then compared against your business info above, and any field that differs is flagged. JavaScript-rendered listings (e.g. Google, Yelp, Facebook) can't be read from static HTML and may not appear here.</p>
            </span>
          </span>
        </h2>
        <p className="cd-help" style={{ margin: "4px 0 0", fontSize: 12.5, color: "var(--ink3)", lineHeight: 1.5 }}>
          Searches Google for external pages that mention your domain (<code style={{ fontSize: 11, background: "var(--bg3)", padding: "1px 5px", borderRadius: 3 }}>"example.com" -site:example.com</code>, top 80) or your business name (top 50),
          then keeps only pages that show at least two of your business name, phone, website (written out or linked), or address — isolating likely citations. Any field that differs from your business info is flagged.
        </p>
      </div>

      {/* Business info (editable — the comparison baseline) */}
      <div style={{ background: "var(--bg2)", border: "1px solid var(--bdr)", borderRadius: 8, padding: "14px 16px", marginTop: 14 }}>
        <div style={{ fontSize: 13, fontWeight: 700, textTransform: "uppercase", letterSpacing: .6, color: "var(--ink3)", marginBottom: 4 }}>Your Business Information</div>
        <div style={{ fontSize: 11, color: "var(--ink4)", marginBottom: 10 }}>Edit any field — these are the values each listing is checked against. Seeded from the cover; changes are saved for this site.</div>
        {editRow("Name", "name", "Business name")}
        {editRow("Website", "website", "https://example.com")}
        {editRow("Address", "address", "Full street address")}
        {editRow("City", "city", "City, ST")}
        {editRow("Phone", "phone", "(555) 555-5555")}
      </div>

      {/* Run control */}
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginTop: 16 }}>
        <button
          className="run-crawl-btn"
          onClick={() => void run()}
          disabled={running}
          // Match the tab toolbar's "Run Crawl" button for consistency (incl. hover).
          style={{
            padding: "9px 22px",
            background: running ? "#94a3b8" : "linear-gradient(135deg, #ea580c, #f97316)",
            color: "#fff",
            border: "none",
            borderRadius: 0,
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: ".3px",
            cursor: running ? "default" : "pointer",
            fontFamily: "'Inter', 'Mulish', sans-serif",
            whiteSpace: "nowrap",
            boxShadow: running ? "none" : "0 2px 8px rgba(234, 88, 12, .4)",
            transition: "background .3s ease, box-shadow .15s, transform .15s",
          }}
        >
          {running ? "Checking…" : result ? "Re-run Citation Check" : "Run Citation Check"}
        </button>
        {running && <span className="lsg-spinner" style={{ verticalAlign: "-2px" }} />}
        {status && <span style={{ fontSize: 12, color: status.tone === "err" ? "#dc2626" : "var(--ink2)" }}>{status.text}</span>}
        {!running && result?.ranAt && <span style={{ fontSize: 11, color: "var(--ink4)", marginLeft: "auto" }}>Last run: {result.ranAt}</span>}
      </div>

      {/* Results */}
      {result && (
        <div style={{ marginTop: 22 }}>
          <h3 style={{ margin: "0 0 4px", fontSize: 16, fontWeight: 700, color: "var(--ink)", fontFamily: "'Oswald','Inter',sans-serif", textTransform: "uppercase", letterSpacing: .5 }}>Citations Indexed in Google</h3>
          <p style={{ margin: "0 0 12px", fontSize: 11.5, color: "var(--ink3)" }}>External pages that reference your website and at least one other matching detail — potential citations.</p>
          {(result.searches ?? []).some(s => s.error) && (
            <div style={{ border: "1px solid #fca5a5", background: "rgba(220,38,38,.06)", color: "#b91c1c", borderRadius: 8, padding: "9px 12px", fontSize: 12.5, marginBottom: 12, lineHeight: 1.5 }}>
              <strong>Incomplete scan:</strong>{" "}
              {(result.searches ?? []).filter(s => s.error).map(s => `the ${s.via === "domain" ? "domain" : "business-name"} search failed (${s.error})`).join(" and ")}
              {" "}even after retrying, so citations it would have found are missing. Run the check again.
            </div>
          )}
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14 }}>
            <Stat n={result.counts.kept} label="Citations found" color="#2563eb" />
            <Stat n={result.counts.consistent} label="Consistent" color="#16a34a" />
            <Stat n={result.counts.mismatched} label="Inconsistent" color="#dc2626" />
            <Stat n={result.counts.checked} label="Pages searched" color="#64748b" />
          </div>

          {result.profiles.length === 0 && (
            <div style={{ fontSize: 12.5, color: "var(--ink3)", padding: "10px 0" }}>No potential citations were found — no external page showed your website plus another matching detail (name, phone, or address).</div>
          )}

          {result.profiles.map((p, i) => (
            <div key={`${p.url}-${i}`} style={{ border: `1px solid ${p.hasMismatch ? "#fecaca" : "var(--bdr)"}`, borderLeft: `4px solid ${p.hasMismatch ? "#dc2626" : "#16a34a"}`, borderRadius: 8, padding: "12px 14px", marginBottom: 10, background: p.hasMismatch ? "rgba(220,38,38,.03)" : "var(--bg)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
                <span style={{ fontWeight: 700, fontSize: 13, color: "var(--ink)" }}>{p.domain}</span>
                {p.hasMismatch
                  ? <span style={{ fontSize: 10.5, fontWeight: 700, color: "#dc2626", background: "#fee2e2", padding: "2px 8px", borderRadius: 10 }}>INCONSISTENT</span>
                  : <span style={{ fontSize: 10.5, fontWeight: 700, color: "#16a34a", background: "#dcfce7", padding: "2px 8px", borderRadius: 10 }}>CONSISTENT</span>}
                {p.fromPreview && <span style={{ fontSize: 10.5, color: "var(--ink3)" }}>Read from Google's preview — the site blocked a direct download, so some details may be cut off</span>}
                <a href={p.url} target="_blank" rel="noopener" style={{ fontSize: 11, color: "#2563eb", marginLeft: "auto", textDecoration: "none" }}>↗ View listing</a>
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(230px,1fr))", gap: "4px 18px" }}>
                {FIELD_ORDER.map(({ key: fk, label }) => {
                  const f = p.fields[fk];
                  return (
                    <div key={fk} style={{ display: "flex", gap: 8, fontSize: 12, padding: "3px 0", alignItems: "baseline" }}>
                      <span style={{ width: 58, flexShrink: 0, color: "var(--ink3)", fontWeight: 700, fontSize: 12, textTransform: "uppercase" }}>{label}</span>
                      {f
                        ? <span style={{ flex: 1, minWidth: 0 }}><span style={{ color: f.matches === false ? "#dc2626" : "var(--ink)", wordBreak: "break-word" }}>{f.value}</span> {badge(f.matches)}</span>
                        : <span style={{ flex: 1, color: "var(--ink4)" }}>not listed</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {result.notes.length > 0 && (
            <div style={{ marginTop: 12, fontSize: 11, color: "var(--ink4)", lineHeight: 1.6 }}>
              {result.notes.map((n, i) => <div key={i}>· {n}</div>)}
            </div>
          )}

          {result.pages && (
            <details style={{ marginTop: 14, border: "1px solid var(--bdr)", borderRadius: 8, padding: "8px 12px" }}>
              <summary style={{ cursor: "pointer", fontSize: 12.5, fontWeight: 700, color: "var(--ink2)" }}>All {result.pages.length} pages checked — what happened to each</summary>
              <div style={{ margin: "10px 0 8px", fontSize: 11.5, color: "var(--ink3)", lineHeight: 1.6 }}>
                {(result.searches ?? []).map((s, i) => (
                  <div key={i}>
                    {s.via === "domain" ? "Domain search" : "Business-name search"}: <code style={{ fontSize: 10.5, background: "var(--bg3)", padding: "1px 4px", borderRadius: 3 }}>{s.query}</code>{" "}
                    → {s.error ? <span style={{ color: "#dc2626" }}>failed ({s.error})</span> : `${s.found} external result${s.found === 1 ? "" : "s"}`}
                  </div>
                ))}
              </div>
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11.5 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "var(--ink3)", fontSize: 10.5, textTransform: "uppercase", letterSpacing: .4 }}>
                      <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)" }}>Page</th>
                      <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)", whiteSpace: "nowrap" }}>Found by</th>
                      <th style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)" }}>Result</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.pages.map((pg, i) => {
                      const o = OUTCOME_LABEL[pg.outcome];
                      return (
                        <tr key={`${pg.url}-${i}`}>
                          <td style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)", minWidth: 220 }}>
                            <a href={pg.url} target="_blank" rel="noopener" style={{ color: "#2563eb", textDecoration: "none", fontWeight: 600 }}>{pg.domain}</a>
                            <div style={{ color: "var(--ink3)", fontSize: 10.5, overflowWrap: "anywhere" }}>{pg.title}</div>
                          </td>
                          <td style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)", whiteSpace: "nowrap", color: "var(--ink2)" }}>{pg.via === "domain" ? "Domain" : "Name"} #{pg.rank}</td>
                          <td style={{ padding: "5px 6px", borderBottom: "1px solid var(--bdr)", color: o.color, fontWeight: 600 }}>
                            {o.text}{pg.detail && <span style={{ fontWeight: 400, color: "var(--ink3)" }}> ({pg.detail})</span>}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>
      )}
    </div>
  );
}

function Stat({ n, label, color }: { n: number; label: string; color: string }) {
  return (
    <div style={{ border: "1px solid var(--bdr)", borderRadius: 8, padding: "8px 16px", minWidth: 96, textAlign: "center" }}>
      <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1.1 }}>{n}</div>
      <div style={{ fontSize: 12, fontWeight: 700, textTransform: "uppercase", letterSpacing: .4, color: "var(--ink3)", marginTop: 2 }}>{label}</div>
    </div>
  );
}
